import { clamp, debounce, dropRight, forEach, head, includes, last, memoize, parseInt, uniq } from 'lodash/fp';
import { Field, P, Slider } from '@ahmdigital/ui';
import getIncomeTypeFromScale from '@ahmdigital/logic/lib/utils/get-income-type-from-scale';
import React, { PureComponent } from 'react';

import { Rate } from '../../../../../types/__codegen__/sanity/graphql';
import calculateMls from '../../utils/calculate-mls';
import constants from '../../../../../ahm-constants';
import elementAnalytics from '../../../../../analytics/element';
import getScaleOptions from '../../../../../utils/get-scale-options';
import getThresholdsFromTaxRate from '../calculator-section/utils/get-thresholds-from-tax-rate';
import MlsSliderTip from './components/mls-slider-tip';
import styles from './styles.module.sass';

const amountToDrop = 1;
const radix = 10;

const DROPDOWN_PROPS = {
  size: 'auto',
  type: 'dropdown',
};

const scaleOptions = getScaleOptions();

const bindActionToEvent =
  // @ts-expect-error - Automatic, Please fix when editing this file


    (actionFunction) =>
    // @ts-expect-error - Automatic, Please fix when editing this file
    ({ target: { value } }) => {
      actionFunction(value);
    };

// @ts-expect-error - Automatic, Please fix when editing this file
const analyticsFnGenerator = (label, fn, type) =>
  elementAnalytics(
    {
      analytics: {
        component: 'TaxCalculator',
        label,
      },
    },
    type === 'event' ? bindActionToEvent(fn) : fn,
  );

const tipFormatterGenerator =
  // @ts-expect-error - Automatic, Please fix when editing this file


    ({ limits, scale }) =>
    // @ts-expect-error - Automatic, Please fix when editing this file
    (value) => <MlsSliderTip limits={limits} scale={scale} value={value} />;

const calculateSliderProps = memoize((scale, activeTaxRate) => {
  const incomeType = getIncomeTypeFromScale(scale);
  const isSingleIncome = incomeType === constants.INCOME_TYPE.SINGLE;
  const thresholds = getThresholdsFromTaxRate(activeTaxRate, incomeType);

  const marks = {};
  forEach(
    (threshold) => {
      // @ts-expect-error - Automatic, Please fix when editing this file
      const mark = threshold.max / 1000;
      // @ts-expect-error - Automatic, Please fix when editing this file
      marks[mark] = `${mark.toString()}k`;
    },
    dropRight(amountToDrop, thresholds),
  );

  // @ts-expect-error - Automatic, Please fix when editing this file
  const firstMark = head(thresholds).max / 1000;
  // @ts-expect-error - Automatic, Please fix when editing this file
  const lastMark = last(thresholds).min / 1000;

  const min = firstMark - (isSingleIncome ? 15 : 30);
  const max = lastMark + (isSingleIncome ? 15 : 30);

  return { firstMark, lastMark, marks, max, min, thresholds };
});

type CalculatorProps = {
  activeTaxRate: Rate;
  customer?: {
    income?: number;
    incomeType?: string;
    scale?: string;
    state?: string;
  };
  onChange: (...args: unknown[]) => unknown;
  onIncomeChange?: (...args: unknown[]) => unknown;
  onScaleChange?: (...args: unknown[]) => unknown;
  onStateChange?: (...args: unknown[]) => unknown;
};

class Calculator extends PureComponent<CalculatorProps> {
  // @ts-expect-error - Automatic, Please fix when editing this file
  componentDidMount() {
    // We throttle redux action/reducer/render to improve front end performance.
    // This assumes income is not needed for in-page rendering,
    // but only for maintaining customer state data and analytics purposes.
    const { onIncomeChange, onScaleChange, onStateChange, customer } = this.props;
    // @ts-expect-error - Automatic, Please fix when editing this file
    this.onIncomeChange = debounce(500, (value) => onIncomeChange(value));
    // @ts-expect-error - Automatic, Please fix when editing this file
    this.onScaleChange = analyticsFnGenerator('scale', onScaleChange, 'event');
    // @ts-expect-error - Automatic, Please fix when editing this file
    this.onStateChange = analyticsFnGenerator('state', onStateChange, 'event');

    if (onIncomeChange) {
      onIncomeChange(this.props.customer?.income ?? 0);
    }

    if (customer?.scale && onScaleChange) {
      onScaleChange(customer.scale);
    }
  }

  // @ts-expect-error - Automatic, Please fix when editing this file
  componentDidUpdate(prevProps) {
    const { customer: prevCustomer } = prevProps;
    const { activeTaxRate, customer } = this.props;
    // @ts-expect-error - Automatic, Please fix when editing this file
    const incomeTypes = [customer.incomeType, prevCustomer.incomeType];
    if (uniq(incomeTypes).length === 2 && includes(constants.INCOME_TYPE.SINGLE, incomeTypes)) {
      // @ts-expect-error - Automatic, Please fix when editing this file
      const marks = calculateSliderProps(customer.scale, activeTaxRate);
      // @ts-expect-error - Automatic, Please fix when editing this file
      this.onSliderChange(parseInt(radix, marks.min));
    }
  }

  // @ts-expect-error - Automatic, Please fix when editing this file
  onSliderChange = (value) => {
    const { activeTaxRate, customer, onChange } = this.props;

    // @ts-expect-error - Automatic, Please fix when editing this file
    const { firstMark, lastMark, thresholds } = calculateSliderProps(customer.scale, activeTaxRate);
    // We clamp income for the mls calculation because we want a nice round number for the calculator results,
    // but we use the unclamped income for the income tier calculation because we want it work for rebate tier 3 (which starts at $140,001).
    const income = value * 1000;
    const clampedIncome = clamp(firstMark * 1000, lastMark * 1000, income);

    // @ts-expect-error - Automatic, Please fix when editing this file
    this.onIncomeChange(income);

    const mlsAmount = calculateMls({
      income: clampedIncome,
      thresholds,
    });
    onChange({
      income,
      mlsAmount,
      // @ts-expect-error - Automatic, Please fix when editing this file
      scale: customer.scale,
      // @ts-expect-error - Automatic, Please fix when editing this file
      state: customer.state,
    });
  };

  // @ts-expect-error - Automatic, Please fix when editing this file
  render() {
    const { activeTaxRate, customer } = this.props;
    // @ts-expect-error - Automatic, Please fix when editing this file
    const { firstMark, lastMark, marks, max, min } = calculateSliderProps(customer.scale, activeTaxRate);
    // @ts-expect-error - Automatic, Please fix when editing this file
    const isSingleIncome = customer.incomeType === constants.INCOME_TYPE.SINGLE;

    return (
      <div className={styles.calculator}>
        <div className={styles.fieldsWrapper}>
          <P looksLike="h4" variant="regular">
            If you’re a
          </P>
          <div className={styles.field}>
            <Field
              {...DROPDOWN_PROPS}
              ariaLabel="scale"
              name="scale"
              // @ts-expect-error - Automatic, Please fix when editing this file
              onChange={this.onScaleChange}
              options={scaleOptions}
              // @ts-expect-error - Automatic, Please fix when editing this file
              value={customer.scale}
            />
          </div>
          <P looksLike="h4" variant="regular">
            in
          </P>
          <div className={styles.field}>
            <Field
              {...DROPDOWN_PROPS}
              ariaLabel="state"
              name="state"
              // @ts-expect-error - Automatic, Please fix when editing this file
              onChange={this.onStateChange}
              options={constants.STATE_OPTIONS}
              // @ts-expect-error - Automatic, Please fix when editing this file
              value={customer.state}
            />
          </div>
          <P looksLike="h4" variant="regular">
            and earning
          </P>
        </div>
        <div className={styles.sliderContainer}>
          <Slider
            key={isSingleIncome ? 'slider-single' : 'slider-couple'}
            analytics={{ action: 'on_tax_income_move_slider', component: 'tax' }}
            {...{ marks, max, min }}
            name="income"
            onChange={this.onSliderChange}
            tipFormatter={tipFormatterGenerator({
              limits: { max: Math.floor(lastMark), min: Math.floor(firstMark) },
              // @ts-expect-error - Automatic, Please fix when editing this file
              scale: customer.scale,
            })}
            ariaLabelForHandle="income"
            ariaValueTextFormatterForHandle={() => customer?.income?.toString()}
          />
        </div>
      </div>
    );
  }
}

// @ts-expect-error - Automatic, Please fix when editing this file
Calculator.defaultProps = {
  activeTaxRate: null,
  customer: null,
  onIncomeChange: null,
  onScaleChange: null,
  onStateChange: null,
};

export default Calculator;

export { calculateSliderProps };
